import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { scoringApi } from '../services/api';
import type { ScoringState, ScoringConfig } from '../services/api';

/**
 * ScoringContext — provides scoring state (rune unlocks, Frenzy status, config)
 * to all components in the app tree. Follows the AuthContext pattern.
 *
 * State is loaded from the backend via GET /scoring/state and GET /scoring/config.
 * Config is fetched once per session. State is refreshed after answer submissions
 * and Frenzy state changes.
 */

interface ScoringContextState {
  // Runes
  radahnUnlocked: boolean;
  radahnStreakCount: number;
  rykardUnlocked: boolean;
  rykardEquipped: boolean;
  rykard3xCount: number;
  // Frenzy
  frenzyActive: boolean;
  frenzySessionId: string | null;
  frenzyMultiplier: number;
  frenzyFloor: number;
  frenzySessionEarnings: number;
  // Config
  config: ScoringConfig | null;
  // Loading
  isLoaded: boolean;
}

interface ScoringContextType extends ScoringContextState {
  /** Fetch scoring state from backend and update context */
  fetchScoringState: () => Promise<void>;
  /** Fetch scoring config from backend (cached for session) */
  fetchScoringConfig: () => Promise<void>;
  /** Update scoring state from an API response (avoids extra fetch) */
  updateFromResponse: (updates: Partial<ScoringContextState>) => void;
}

const ScoringContext = createContext<ScoringContextType | undefined>(undefined);

const DEFAULT_STATE: ScoringContextState = {
  radahnUnlocked: false,
  radahnStreakCount: 0,
  rykardUnlocked: false,
  rykardEquipped: false,
  rykard3xCount: 0,
  frenzyActive: false,
  frenzySessionId: null,
  frenzyMultiplier: 1.0,
  frenzyFloor: 1.0,
  frenzySessionEarnings: 0,
  config: null,
  isLoaded: false,
};

interface ScoringProviderProps {
  children: ReactNode;
}

export function ScoringProvider({ children }: ScoringProviderProps) {
  const [state, setState] = useState<ScoringContextState>(DEFAULT_STATE);

  const fetchScoringState = useCallback(async () => {
    try {
      const scoringState: ScoringState = await scoringApi.getState();

      setState((prev) => ({
        ...prev,
        radahnUnlocked: scoringState.radahnRuneUnlocked,
        radahnStreakCount: scoringState.radahnStreakCount,
        rykardUnlocked: scoringState.rykardRuneUnlocked,
        rykardEquipped: scoringState.rykardRuneEquipped,
        rykard3xCount: scoringState.rykard3xCount,
        frenzyActive: scoringState.frenzy?.status === 'active',
        frenzySessionId: scoringState.frenzy?.sessionId ?? null,
        frenzyMultiplier: scoringState.frenzy?.currentMultiplier ?? 1.0,
        frenzyFloor: scoringState.rykardRuneEquipped
          ? (prev.config?.frenzy.rykardFloor ?? 1.5)
          : (prev.config?.frenzy.defaultFloor ?? 1.0),
        frenzySessionEarnings: scoringState.frenzy?.totalEarned ?? 0,
        isLoaded: true,
      }));
    } catch {
      // Silently fail — scoring state is non-critical for app function
      setState((prev) => ({ ...prev, isLoaded: true }));
    }
  }, []);

  const fetchScoringConfig = useCallback(async () => {
    try {
      const config = await scoringApi.getConfig();
      setState((prev) => ({ ...prev, config }));
    } catch {
      // Silently fail — config will use defaults
    }
  }, []);

  const updateFromResponse = useCallback((updates: Partial<ScoringContextState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const value: ScoringContextType = {
    ...state,
    fetchScoringState,
    fetchScoringConfig,
    updateFromResponse,
  };

  return <ScoringContext.Provider value={value}>{children}</ScoringContext.Provider>;
}

/**
 * Hook to access scoring state and actions.
 * Must be used within a ScoringProvider.
 */
export function useScoring(): ScoringContextType {
  const context = useContext(ScoringContext);
  if (context === undefined) {
    throw new Error('useScoring must be used within a ScoringProvider');
  }
  return context;
}

export default ScoringContext;
